import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { alpha, useTheme } from "@mui/material/styles";
import { qualificationLabel } from "./displayHelpers";
import {
  searchResultsDocs,
  type PlayerSearchHit,
} from "./searchHelpers";
import type { PlayerDirectory, ResultsDoc } from "./types";
import styles from "./Resultats.module.css";

export interface ResultsSearchSelection {
  licence: string;
  tableauId: string;
  poolId: string | null;
}

interface ResultsSearchProps {
  docs: ResultsDoc[];
  boards?: { name: string; label: string }[];
  directory: PlayerDirectory;
  onSelect: (selection: ResultsSearchSelection) => void;
  selectedLicence?: string | null;
}

function playerSecondary(hit: PlayerSearchHit): string {
  const parts: string[] = [];
  if (hit.club) parts.push(hit.club);
  if (hit.points != null) parts.push(`${hit.points} pts`);
  if (hit.poolLabel) {
    parts.push(
      hit.rank != null ? `${hit.poolLabel} · #${hit.rank}` : hit.poolLabel
    );
  }
  const qual = qualificationLabel(hit.qualification);
  if (qual) parts.push(qual);
  parts.push(hit.tableauLabel);
  return parts.join(" · ");
}

export default function ResultsSearch({
  docs,
  boards = [],
  directory,
  onSelect,
  selectedLicence = null,
}: ResultsSearchProps): React.ReactElement {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const inputId = useId();
  const listId = useId();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [clubExpanded, setClubExpanded] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const hits = useMemo(
    () => searchResultsDocs(docs, query, directory, boards),
    [docs, query, directory, boards]
  );

  const flatPlayers: PlayerSearchHit[] = useMemo(() => {
    const out: PlayerSearchHit[] = [];
    for (const hit of hits) {
      if (hit.kind === "player") out.push(hit);
      else if (hit.clubKey === clubExpanded) out.push(...hit.players);
    }
    return out;
  }, [hits, clubExpanded]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, clubExpanded]);

  useEffect(() => {
    function onDocClick(event: MouseEvent): void {
      if (!wrapRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  function selectPlayer(hit: PlayerSearchHit): void {
    onSelect({
      licence: hit.licence,
      tableauId: hit.tableauId,
      poolId: hit.poolId,
    });
    setQuery(hit.displayName);
    setOpen(false);
  }

  function onKeyDown(event: React.KeyboardEvent): void {
    if (!open && (event.key === "ArrowDown" || event.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!flatPlayers.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flatPlayers.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const hit = flatPlayers[activeIndex];
      if (hit) selectPlayer(hit);
    }
  }

  return (
    <Box ref={wrapRef} className={styles.searchWrap}>
      <TextField
        id={inputId}
        fullWidth
        size="small"
        label="Rechercher un joueur ou un club"
        placeholder="Nom, prénom, licence, club…"
        value={query}
        autoComplete="off"
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
          setClubExpanded(null);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        inputProps={{
          "aria-controls": open ? listId : undefined,
          "aria-expanded": open,
          "aria-autocomplete": "list",
          role: "combobox",
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
          endAdornment: query ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="Effacer la recherche"
                size="small"
                onClick={() => {
                  setQuery("");
                  setOpen(false);
                  setClubExpanded(null);
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
        sx={{
          bgcolor: isDark
            ? alpha(theme.palette.common.white, 0.04)
            : alpha(theme.palette.common.white, 0.7),
          borderRadius: 2.5,
          "& .MuiOutlinedInput-root": {
            borderRadius: 2.5,
            transition: "box-shadow 160ms ease",
            "&.Mui-focused": {
              boxShadow: `0 0 0 3px ${alpha(
                isDark
                  ? theme.palette.secondary.main
                  : theme.palette.primary.main,
                isDark ? 0.28 : 0.18
              )}`,
            },
          },
        }}
      />

      {open && query.trim().length > 0 && (
        <Paper
          id={listId}
          role="listbox"
          aria-label="Résultats de recherche"
          elevation={0}
          className={styles.searchDropdown}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper,
            boxShadow: isDark
              ? `0 16px 40px ${alpha(theme.palette.common.black, 0.45)}`
              : `0 16px 40px ${alpha(theme.palette.common.black, 0.12)}`,
          }}
        >
          {hits.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2, py: 1.5 }}
            >
              Aucun résultat pour « {query.trim()} »
            </Typography>
          ) : (
            <List dense disablePadding sx={{ maxHeight: 320, overflow: "auto" }}>
              {hits.map((hit) => {
                if (hit.kind === "club") {
                  return (
                    <Box key={`club-${hit.clubKey}`}>
                      <ListItemButton
                        onClick={() =>
                          setClubExpanded((cur) =>
                            cur === hit.clubKey ? null : hit.clubKey
                          )
                        }
                        sx={{ py: 1 }}
                      >
                        <ListItemText
                          primary={hit.clubLabel}
                          secondary={`${hit.players.length} joueur${
                            hit.players.length > 1 ? "s" : ""
                          }`}
                          primaryTypographyProps={{
                            fontWeight: 700,
                            fontSize: "0.9rem",
                          }}
                          secondaryTypographyProps={{ fontSize: "0.75rem" }}
                        />
                      </ListItemButton>
                      {clubExpanded === hit.clubKey &&
                        hit.players.map((player) => {
                          const active =
                            flatPlayers[activeIndex]?.licence ===
                              player.licence &&
                            flatPlayers[activeIndex]?.tableauId ===
                              player.tableauId;
                          return (
                            <ListItemButton
                              key={`p-${player.licence}-${player.tableauId}`}
                              selected={
                                player.licence === selectedLicence || active
                              }
                              onClick={() => selectPlayer(player)}
                              sx={{ py: 1, pl: 3 }}
                            >
                              <ListItemText
                                primary={player.displayName}
                                secondary={playerSecondary(player)}
                                primaryTypographyProps={{
                                  fontWeight: 600,
                                  fontSize: "0.9rem",
                                }}
                                secondaryTypographyProps={{
                                  fontSize: "0.75rem",
                                  color: "text.secondary",
                                }}
                              />
                            </ListItemButton>
                          );
                        })}
                    </Box>
                  );
                }

                const active =
                  flatPlayers[activeIndex]?.licence === hit.licence &&
                  flatPlayers[activeIndex]?.tableauId === hit.tableauId;
                return (
                  <ListItemButton
                    key={`p-${hit.licence}-${hit.tableauId}`}
                    selected={hit.licence === selectedLicence || active}
                    onClick={() => selectPlayer(hit)}
                    sx={{ py: 1.1 }}
                  >
                    <ListItemText
                      primary={hit.displayName}
                      secondary={playerSecondary(hit)}
                      primaryTypographyProps={{
                        fontWeight: 600,
                        fontSize: "0.9rem",
                      }}
                      secondaryTypographyProps={{
                        fontSize: "0.75rem",
                        color: "text.secondary",
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Paper>
      )}
    </Box>
  );
}
